import classNames from 'classnames';
import React, { PropsWithChildren } from 'react';

export type ButtonSizes = 'xs' | 'sm' | 'md' | 'base' | 'lg' | 'xl';
export type ButtonVariants =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'rainbow'
  | 'link'
  | 'link-body'
  | 'unstyled'
  | 'bordered-body'
  | 'success'
  | 'warning'
  | 'orange';

type Props = {
  variant?: ButtonVariants;
  size?: ButtonSizes;
  fullWidth?: 'all' | 'desktop' | 'mobile';
  disabled?: boolean;
  className?: string;
  onClick?: (...props: any) => any;
  href?: string;
  isInternalLink?: boolean;
  underline?: boolean;
  noPadding?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

type ButtonHTMLTagProps = {
  href?: string;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  onClick?: (...props: any) => any;
  disabled?: boolean;
  isInternalLink?: boolean;
} & React.HTMLAttributes<HTMLButtonElement | HTMLAnchorElement>;

const Button: React.FC<PropsWithChildren<Props>> = ({
  variant = 'primary',
  size = 'sm',
  fullWidth,
  disabled,
  className = '',
  onClick,
  href,
  isInternalLink = false,
  type,
  underline,
  noPadding = false,
  children,
  ...rest
}: PropsWithChildren<Props>) => {
  return (
    <ButtonHTMLTag
      type={type}
      href={href}
      disabled={disabled}
      isInternalLink={isInternalLink}
      onClick={onClick}
      className={classNames(
        't-inline-flex t-cursor-pointer t-items-center t-justify-center t-rounded-button t-font-body t-font-bold t-uppercase t-duration-300 focus:t-outline-none active:t-outline-none',
        {
          't-no-underline hover:t-no-underline': underline === false,
        },
        {
          't-cursor-not-allowed t-border-2 t-border-solid t-border-neutral-300 !t-bg-neutral-300 !t-text-neutral-500 hover:t-bg-neutral-300 hover:!t-text-neutral-500 hover:t-opacity-100':
            disabled,
        },
        {
          't-w-full': fullWidth === 'all',
          't-w-auto lg:t-w-full': fullWidth === 'desktop',
          't-w-full lg:t-w-auto': fullWidth === 'mobile',
        },
        {
          't-text-xs': size === 'xs' || size === 'sm' || size === 'md',
          't-text-sm': size === 'base',
          't-text-base': size === 'lg',
          't-text-lg': size === 'xl',
        },
        {
          't-px-3 t-py-2': !noPadding && size === 'xs',
          't-px-4 t-py-2 ': !noPadding && size === 'sm',
          't-px-6 t-py-3': !noPadding && (size === 'md' || size === 'base'),
          't-px-8 t-py-3 md:t-px-12': !noPadding && size === 'lg',
          't-px-8 t-py-4 md:t-px-12': !noPadding && size === 'xl',
        },
        {
          't-border-2 t-border-solid t-border-[var(--primary-button-bg-color)] !t-bg-[var(--primary-button-bg-color)] t-text-[var(--primary-button-text-color)] t-no-underline hover:t-text-[var(--primary-button-text-color)] hover:t-no-underline hover:t-opacity-75':
            variant === 'primary' && !disabled,
          't-border-2 t-border-solid t-border-[#eabb18] !t-bg-[#eabb18] t-text-brand-black t-no-underline hover:t-text-brand-black hover:t-no-underline hover:t-opacity-75':
            variant === 'warning' && !disabled,
          't-border-brand-orange !t-bg-brand-orange t-border-2 t-border-solid t-text-white t-no-underline hover:t-text-white hover:t-no-underline hover:t-opacity-75':
            variant === 'orange' && !disabled,
          't-border-2 t-border-solid t-border-[var(--primary-button-bg-color)] t-bg-transparent t-text-brand-black t-no-underline hover:t-bg-[var(--primary-button-bg-color)] hover:t-text-white hover:t-no-underline':
            variant === 'secondary' && !disabled,
          't-border-2 t-border-solid t-border-neutrals-graphite !t-bg-white t-text-neutrals-graphite t-no-underline hover:t-text-neutrals-graphite hover:t-no-underline hover:t-opacity-75':
            variant === 'tertiary' && !disabled,
          'rainbow-btn t-border-2 t-border-solid t-border-transparent t-bg-[length:250px_250px] t-text-white t-no-underline hover:t-text-white hover:t-no-underline hover:t-opacity-75':
            variant === 'rainbow' && !disabled,
          't-border-2 t-border-solid t-border-[#316E0B] t-bg-[#316E0B] t-text-white hover:t-text-white hover:t-opacity-75':
            variant === 'success' && !disabled,
          't-border-none t-bg-transparent t-font-normal !t-normal-case t-text-black !t-underline t-opacity-60 hover:!t-text-black hover:t-opacity-100':
            variant === 'link' && !disabled,
          't-border-none t-bg-transparent t-font-normal !t-normal-case t-text-body !t-underline t-opacity-100 hover:t-opacity-75':
            variant === 'link-body' && !disabled,
          't-border-none t-bg-transparent t-text-body t-no-underline t-opacity-100 hover:t-text-body hover:t-no-underline hover:t-opacity-75':
            variant === 'unstyled' && !disabled,
          't-border-2 t-border-solid t-border-body t-bg-transparent t-text-body t-no-underline hover:t-bg-brand-black hover:t-text-white hover:t-no-underline':
            variant === 'bordered-body' && !disabled,
        },

        className
      )}
      {...rest}>
      {children}
    </ButtonHTMLTag>
  );
};

const ButtonHTMLTag: React.FC<ButtonHTMLTagProps> = ({
  href,
  type,
  onClick,
  children,
  isInternalLink,
  ...rest
}: PropsWithChildren<ButtonHTMLTagProps>) =>
  href ? (
    <a href={href} role='button' onClick={onClick} {...rest}>
      {children}
    </a>
  ) : (
    <button onClick={onClick} {...rest} type={type}>
      {children}
    </button>
  );

export default Button;
